import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showErrorToast, showSuccessToast } from '@Helpers';
import { cn } from '@Utils';

import Select, { SelectOptions } from '@Components/Select/select';
import Modal from '@Components/ui/modal';
import { Button } from '@Components/ui-v2/button';
import { Input } from '@Components/ui-v2/input';
import {
  Select as CompactSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@Components/ui-v2/select';
import { useFetchData } from '@CustomHooks';
import { PostScheduleTaskInput, Product, TaskType } from '@Models';
import { bopsApi } from '@Network';
import { UPPERCASE_LABEL_CLASS_NAME } from '../../FieldServices/constants';
import { COMPACT_SELECT_TRIGGER_CLASS_NAME, getErrorDetail } from '../constants';
import { buildCategoryRefs, getCategoryOptions, getTaskTypeName } from '../Wizard/constants';

interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  scheduleId: string;
  weekStart: string | null;
  retailers: string[];
  availableTaskTypes: TaskType[];
  onCreated: () => Promise<void>;
}

type CreateScheduleTaskInput = PostScheduleTaskInput & { schedule_id: string };

const buildCreateInputs = (
  base: Omit<CreateScheduleTaskInput, 'category_refs'>,
  categories: string[],
  retailers: string[],
  products: Product[],
): CreateScheduleTaskInput[] => {
  if (categories.length === 0) return [{ ...base, category_refs: null }];
  return categories.map((category) => ({
    ...base,
    category_refs: buildCategoryRefs([category], retailers, products),
  }));
};

// Above ui/modal's own stacking layer (z-[999999]), or the dropdowns open behind it.
const MODAL_MENU_Z_INDEX = 1000000;

const LABEL_CLASS_NAME = cn('mb-1.5 block', UPPERCASE_LABEL_CLASS_NAME);
const FOOTER_BUTTON_CLASS_NAME = 'h-9 text-xs font-semibold';

const createTasksInOrder = async (
  inputs: CreateScheduleTaskInput[],
  onEachCreated: (createdCount: number) => void,
  createdCount = 0,
): Promise<void> => {
  const [first, ...rest] = inputs;
  if (!first) return;
  await bopsApi.createScheduleTask(first);
  onEachCreated(createdCount + 1);
  await createTasksInOrder(rest, onEachCreated, createdCount + 1);
};

const AddTaskModal = ({
  isOpen,
  onClose,
  scheduleId,
  weekStart,
  retailers,
  availableTaskTypes,
  onCreated,
}: AddTaskModalProps) => {
  const { t } = useTranslation();
  const [taskTypeId, setTaskTypeId] = useState<string | null>(null);
  const [area, setArea] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchProducts = useCallback(
    async () => (await Promise.all(retailers.map((retailer) => bopsApi.getProducts(retailer)))).flat(),
    [retailers],
  );
  const {
    data: products,
    isLoading: isLoadingProducts,
    error: productsError,
    refetch: refetchProducts,
  } = useFetchData(fetchProducts, null, true);

  useEffect(() => {
    if (!isOpen) return;
    refetchProducts().catch(() => {});
  }, [isOpen]);

  const hasTaskTypes = availableTaskTypes.length > 0;
  const taskTypeOptions = useMemo(
    () => availableTaskTypes.map((taskType) => ({ value: taskType.id, label: getTaskTypeName(taskType, t) })),
    [availableTaskTypes, t],
  );
  const categoryOptions: SelectOptions[] = useMemo(
    () => getCategoryOptions(products ?? [], retailers).map((category) => ({ value: category, label: category })),
    [products, retailers],
  );

  const selectedTaskType = availableTaskTypes.find((taskType) => taskType.id === taskTypeId) ?? null;
  const requiresProducts = selectedTaskType?.requires_products ?? false;
  const canSubmit = !!selectedTaskType && (!requiresProducts || selectedCategories.length > 0);

  const reset = () => {
    setTaskTypeId(null);
    setArea('');
    setSelectedCategories([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!taskTypeId) return;
    setIsSubmitting(true);
    const inputs = buildCreateInputs(
      { schedule_id: scheduleId, task_type_id: taskTypeId, area: area.trim() || null, week_start: weekStart },
      selectedCategories,
      retailers,
      products ?? [],
    );
    let createdCount = 0;
    try {
      await createTasksInOrder(inputs, (count) => {
        createdCount = count;
      });
      showSuccessToast(t('Schedule.Detail.AddTask.Success'));
      handleClose();
    } catch (error) {
      showErrorToast(getErrorDetail(error) ?? t('Schedule.Detail.AddTask.Error'));
      if (inputs.length > 1) setSelectedCategories(selectedCategories.slice(createdCount));
    } finally {
      await onCreated();
      setIsSubmitting(false);
    }
  };

  const renderShell = (body: ReactNode, footer: ReactNode) => (
    <Modal modalOpen={isOpen} closeModal={handleClose} className="max-w-[520px] items-stretch justify-start gap-0 p-0">
      <div className="cursor-default border-b border-gray-200 px-5 py-4 pr-12">
        <h2 className="text-[15px] font-bold text-gray-900">{t('Schedule.Detail.AddTask.Title')}</h2>
      </div>
      <div className="flex cursor-default flex-col gap-4 px-5 py-4">{body}</div>
      <div className="flex cursor-default justify-end gap-2 border-t border-gray-200 px-5 py-3">{footer}</div>
    </Modal>
  );

  if (!hasTaskTypes) {
    return renderShell(
      <p className="rounded-md border border-gray-200 bg-gray-50 p-2.5 text-xs text-gray-500">
        {t('Schedule.Detail.AddTask.NoPlannableTaskTypes')}
      </p>,
      <Button variant="outline" onClick={handleClose} className={FOOTER_BUTTON_CLASS_NAME}>
        {t('Global.Close')}
      </Button>,
    );
  }

  return renderShell(
    <>
      <div>
        <span className={LABEL_CLASS_NAME}>{t('Schedule.Detail.AddTask.TaskType')}</span>
        <CompactSelect
          value={taskTypeId ?? ''}
          onValueChange={(next) => {
            setTaskTypeId(next);
            setSelectedCategories([]);
          }}>
          <SelectTrigger
            aria-label={t('Schedule.Detail.AddTask.TaskType')}
            className={cn(COMPACT_SELECT_TRIGGER_CLASS_NAME, 'w-full')}>
            <SelectValue placeholder={t('Schedule.Detail.AddTask.TaskTypePlaceholder')} />
          </SelectTrigger>
          <SelectContent style={{ zIndex: MODAL_MENU_Z_INDEX }}>
            {taskTypeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value} className="text-xs">
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </CompactSelect>
      </div>

      <div>
        <span className={LABEL_CLASS_NAME}>{t('Schedule.Detail.AddTask.Categories')}</span>
        {/* Multi-select stays on react-select: the ui-v2 Select picks a single value. */}
        <div aria-disabled={!taskTypeId} className={cn('text-xs', !taskTypeId && 'pointer-events-none opacity-60')}>
          <Select
            options={categoryOptions}
            isMulti
            value={categoryOptions.filter((option) => selectedCategories.includes(option.value))}
            onChange={(options: SelectOptions[]) =>
              setSelectedCategories((options ?? []).map((option) => option.value))
            }
            placeholder={
              taskTypeId
                ? t('Schedule.Detail.AddTask.CategoriesPlaceholder')
                : t('Schedule.Detail.AddTask.CategoriesSelectTypeFirst')
            }
            ariaLabel={t('Schedule.Detail.AddTask.Categories')}
            menuZIndex={MODAL_MENU_Z_INDEX}
          />
        </div>
        {taskTypeId && isLoadingProducts && (
          <p className="mt-1 text-xs text-gray-500">{t('Schedule.Detail.AddTask.CategoriesLoading')}</p>
        )}
        {taskTypeId && productsError && (
          <p className="mt-1 text-xs text-error-600">{t('Schedule.Detail.AddTask.CategoriesError')}</p>
        )}
        {taskTypeId && !isLoadingProducts && !productsError && (
          <p className="mt-1 text-xs text-gray-500">
            {requiresProducts
              ? t('Schedule.Detail.AddTask.CategoriesHint')
              : t('Schedule.Detail.AddTask.CategoriesOptional')}
          </p>
        )}
      </div>

      <div>
        <span className={LABEL_CLASS_NAME}>{t('Schedule.Detail.AddTask.Area')}</span>
        <Input
          value={area}
          maxLength={64}
          onChange={(event) => setArea(event.target.value)}
          placeholder={t('Schedule.Detail.AddTask.AreaPlaceholder')}
          aria-label={t('Schedule.Detail.AddTask.Area')}
          className="h-9 rounded-lg border-gray-300 text-xs md:text-xs"
        />
      </div>
    </>,
    <>
      <Button variant="outline" onClick={handleClose} disabled={isSubmitting} className={FOOTER_BUTTON_CLASS_NAME}>
        {t('Global.Canceled')}
      </Button>
      <Button onClick={handleSubmit} disabled={!canSubmit || isSubmitting} className={FOOTER_BUTTON_CLASS_NAME}>
        {t('Schedule.Detail.AddTask.Submit')}
      </Button>
    </>,
  );
};

export default AddTaskModal;
